if (!customElements.get('collection-list')) {
    class CollectionList extends HTMLElement {
        constructor() {
            super();
            this._autoSlideTimer = null;
        }

        connectedCallback() {
            this.selectors = {
                sliderWrapper: '.collection-list__items',
                pagination: '.swiper-pagination',
                nextEl: '.swiper-btn-next',
                prevEl: '.swiper-btn-prev',
            };
            this.classes = {
                grid: 'f-grid',
                swiper: 'swiper',
                swiperWrapper: 'swiper-wrapper',
            };

            this.sectionId = this.dataset.sectionId;
            this.section = this.closest(`.section-${this.sectionId}`);
            this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

            this.enableSlider = this.dataset.enableSlider === 'true';
            this.items = parseInt(this.dataset.items) || 4;
            this.tabletItems = parseInt(this.dataset.tabletItems) || 2;
            this.mobileItems = parseInt(this.dataset.mobileItems) || 1;

            // Auto slide settings
            this.enableAutoSlide = this.dataset.autoSlide === 'true';
            this.autoSlideDelay = parseInt(this.dataset.autoSlideDelay) || 3000;
            this.sliderSpeed = parseInt(this.dataset.sliderSpeed) || 400;

            // Arrow visibility
            this.showArrows = this.dataset.showArrows !== 'false';

            this.sliderInstance = false;

            if (!this.enableSlider) return;

            this.init();

            document.addEventListener('matchMobile', () => {
                this.init();
            });
            document.addEventListener('unmatchMobile', () => {
                this.init();
            });
        }

        init() {
            // Always init the slider on every screen size so auto-slide
            // works on mobile and tablet too. Responsive slidesPerView is
            // handled via Swiper breakpoints instead of destroying the slider.
            this.initSlider();
        }

        initSlider() {
            const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

            // Build navigation config only when arrows are shown
            const navigationConfig = this.showArrows
                ? {
                    nextEl: this.section ? this.section.querySelector(this.selectors.nextEl) : null,
                    prevEl: this.section ? this.section.querySelector(this.selectors.prevEl) : null,
                }
                : false;

            const sliderOptions = {
                // Default (mobile-first): use mobile column count
                slidesPerView: this.mobileItems,
                // Fixed 12px gap on mobile so cards don't touch each other
                spaceBetween: 12,
                navigation: navigationConfig,
                pagination: false,
                speed: this.sliderSpeed,
                breakpoints: {
                    // Tablet (768px+): switch to theme column-gap
                    768: {
                        slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
                        spaceBetween: columnGap.tablet,
                    },
                    // Large tablet / small desktop (1024px+)
                    1024: {
                        slidesPerView: parseInt(this.items),
                        spaceBetween: columnGap.tabletLarge,
                    },
                    // Full desktop (1280px+)
                    1280: {
                        slidesPerView: parseInt(this.items),
                        spaceBetween: columnGap.desktop,
                    },
                },
                loop: true,
                threshold: 2,
            };

            if (this.enableAutoSlide) {
                sliderOptions.autoplay = {
                    delay: this.autoSlideDelay,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                };
            }

            if (typeof this.sliderInstance !== 'object') {
                this.classList.add(this.classes.swiper);
                this.sliderWrapper.classList.remove(this.classes.grid);
                this.sliderWrapper.classList.add(this.classes.swiperWrapper);
                this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
                this.sliderInstance.init();

                // Start manual auto-slide after slider is ready
                this._startAutoSlide();

                const focusableElements = FoxTheme.a11y.getFocusableElements(this);
                focusableElements.forEach((element) => {
                    element.addEventListener('focusin', () => {
                        const slide = element.closest('.swiper-slide');
                        this.sliderInstance.slider.slideTo(
                            this.sliderInstance.slider.slides.indexOf(slide)
                        );
                    });
                });
            }

            if (Shopify.designMode && typeof this.sliderInstance === 'object') {
                document.addEventListener('shopify:block:select', (e) => {
                    if (e.detail.sectionId != this.sectionId) return;
                    const { target } = e;
                    const index = Number(target.dataset.index);
                    this.sliderInstance.slider.slideToLoop(index);
                });
            }
        }

        /**
         * Manually drive auto-slide using setInterval + slideNext().
         * This works regardless of whether the theme's Swiper bundle
         * includes the Autoplay module.
         */
        _startAutoSlide() {
            this._stopAutoSlide(); // clear any existing timer first

            if (!this.enableAutoSlide) return;
            if (typeof this.sliderInstance !== 'object') return;

            const swiper = this.sliderInstance.slider;
            if (!swiper) return;

            // If native autoplay is running, do not use manual setInterval
            if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
                return;
            }

            this._autoSlideTimer = setInterval(() => {
                // Only advance when the page/tab is visible to avoid drifting
                if (document.visibilityState === 'hidden') return;
                if (swiper.destroyed) {
                    this._stopAutoSlide();
                    return;
                }
                swiper.slideNext();
            }, this.autoSlideDelay);

            // Pause on mouse enter/touch, resume on leave/release
            this.addEventListener('mouseenter', () => this._stopAutoSlide());
            this.addEventListener('mouseleave', () => this._startAutoSlide());
            this.addEventListener('touchstart', () => this._stopAutoSlide(), { passive: true });
            this.addEventListener('touchend', () => this._startAutoSlide(), { passive: true });
        }

        _stopAutoSlide() {
            if (this._autoSlideTimer) {
                clearInterval(this._autoSlideTimer);
                this._autoSlideTimer = null;
            }
        }

        destroySlider() {
            this._stopAutoSlide();
            this.classList.remove(this.classes.swiper);
            this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
            this.sliderWrapper.classList.add(this.classes.grid);
            if (typeof this.sliderInstance === 'object') {
                this.sliderInstance.slider.destroy();
                this.sliderInstance = false;
            }
        }
    }
    customElements.define('collection-list', CollectionList);
}
